import { signOut } from 'firebase/auth';

import { selectedSettings } from 'common/config/firebase-config';
import type {
  AdminApiFieldErrors,
  AdminApiMethod,
  AdminApiMethodToDefinition,
  AdminApiResponse,
} from 'common/models/admin-api.interface';

import { getFirebaseAuth } from './firebase';

export class AdminApiClientError extends Error {
  constructor(
    message: string,
    public readonly status: number,
    public readonly fieldErrors?: AdminApiFieldErrors,
  ) {
    super(message);
    this.name = 'AdminApiClientError';
  }
}

async function getAdminApiUrl(): Promise<string> {
  if (typeof __USE_LOCAL_AUTH__ !== 'undefined' && __USE_LOCAL_AUTH__) {
    return `http://localhost:${__EMULATOR_FUNCTIONS_PORT__}/${selectedSettings.firebaseConfig.projectId}/us-central1/adminApi`;
  }
  return `${selectedSettings.firebaseCloudFunctionUrlOrigin}/adminApi`;
}

export async function adminApiFetch<M extends AdminApiMethod>(
  method: M,
  body?: AdminApiMethodToDefinition[M]['options'],
  options: { signal?: AbortSignal } = {},
): Promise<AdminApiMethodToDefinition[M]['data']> {
  const user = getFirebaseAuth().currentUser;
  if (!user) throw new Error('Not authenticated');

  const token = await user.getIdToken();
  const baseUrl = await getAdminApiUrl();

  const response = await fetch(`${baseUrl}/${method}`, {
    method: 'POST',
    signal: options.signal,
    headers: {
      'Content-Type': 'application/json',
      Authorization: `Bearer ${token}`,
    },
    body: body ? JSON.stringify(body) : undefined,
  });

  let result: AdminApiResponse<AdminApiMethodToDefinition[M]['data']> | undefined;
  try {
    result = (await response.json()) as AdminApiResponse<AdminApiMethodToDefinition[M]['data']>;
  } catch {
    result = undefined;
  }

  if (response.status === 401 || response.status === 403) {
    await signOut(getFirebaseAuth());
    throw new Error(result?.error || 'Not authorized');
  }
  if (!response.ok) {
    throw new AdminApiClientError(
      result?.error || `API request failed (${response.status})`,
      response.status,
      result?.fieldErrors,
    );
  }
  if (!result?.isOk) {
    throw new AdminApiClientError(
      result?.error || 'API request failed',
      response.status,
      result?.fieldErrors,
    );
  }
  return result.data as AdminApiMethodToDefinition[M]['data'];
}

export const fleetDrainPod = (options: AdminApiMethodToDefinition['fleetDrainPod']['options']) =>
  adminApiFetch('fleetDrainPod', options);

export const fleetStopPod = (options: AdminApiMethodToDefinition['fleetStopPod']['options']) =>
  adminApiFetch('fleetStopPod', options);

export const fleetLaunchPod = (options: AdminApiMethodToDefinition['fleetLaunchPod']['options']) =>
  adminApiFetch('fleetLaunchPod', options);

export const fleetSetAutoscaleSettings = (
  options: AdminApiMethodToDefinition['fleetSetAutoscaleSettings']['options'],
) => adminApiFetch('fleetSetAutoscaleSettings', options);

export const fleetSetRegionConfig = (
  options: AdminApiMethodToDefinition['fleetSetRegionConfig']['options'],
) => adminApiFetch('fleetSetRegionConfig', options);

export const fleetClearRegionPodTypeConfig = (
  options: AdminApiMethodToDefinition['fleetClearRegionPodTypeConfig']['options'],
) => adminApiFetch('fleetClearRegionPodTypeConfig', options);

export const getFleetRegions = (requestOptions: { signal?: AbortSignal } = {}) =>
  adminApiFetch('getFleetRegions', {}, requestOptions);

export const fleetSetRegionRequired = (
  options: AdminApiMethodToDefinition['fleetSetRegionRequired']['options'],
) => adminApiFetch('fleetSetRegionRequired', options);

export const fleetSetTaskLaunchesEnabled = (
  options: AdminApiMethodToDefinition['fleetSetTaskLaunchesEnabled']['options'],
) => adminApiFetch('fleetSetTaskLaunchesEnabled', options);

export const fleetClearCooldown = (options: AdminApiMethodToDefinition['fleetClearCooldown']['options']) =>
  adminApiFetch('fleetClearCooldown', options);

export const getFleetAdminAudit = (
  options: AdminApiMethodToDefinition['getFleetAdminAudit']['options'] = {},
  requestOptions: { signal?: AbortSignal } = {},
) => adminApiFetch('getFleetAdminAudit', options, requestOptions);
